import { useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button, Card, Chip, Page, PageHeader, StickyFooter } from "@/components/kit";

const key = (t) => (t ? `${t.kind}:${t.id}` : "");
const FILTERS = [{ id: "all", label: "Todos" }, { id: "review", label: "Para revisar" }, { id: "none", label: "Sem correspondência" }];

function ScoreBadge({ row }) {
  if (row.already) return <span className="badge badge-muted" data-testid={`code-already-${row.code}`}>Já cadastrado</span>;
  if (!row.match) return <span className="badge badge-danger">Sem correspondência</span>;
  if (row.score >= 0.999) return <span className="badge badge-success">Nome igual</span>;
  return <span className={`badge badge-${row.score >= 0.85 ? "success" : "warning"}`}>Parecido {Math.round(row.score * 100)}%</span>;
}

function CodeRow({ row, choice, setChoice, checked, toggle, targets, dup }) {
  return (
    <div className="flex flex-wrap items-center gap-3 py-3" data-testid={`code-row-${row.code}`}>
      <input type="checkbox" className="w-4 h-4" checked={checked} disabled={!choice} onChange={toggle} data-testid={`code-check-${row.code}`} />
      <div className="flex-1 min-w-[160px]">
        <div className="label">{row.name}</div>
        <div className="caption flex flex-wrap items-center gap-2">Cód. <b className="tnum">{row.code}</b> <ScoreBadge row={row} /></div>
        {row.code_owner && <div className="caption flex items-center gap-1 mt-1" style={{ color: "var(--accent)" }}><AlertTriangle size={12} /> Código já usado por “{row.code_owner.name}”</div>}
        {row.match?.sales_code && !row.already && <div className="caption mt-1" style={{ color: "var(--accent)" }}>Vai substituir o código atual {row.match.sales_code}</div>}
        {dup && <DupWarning />}
      </div>
      <select className="input input-sm w-full sm:w-72" value={choice} onChange={(e) => setChoice(e.target.value)} data-testid={`code-target-${row.code}`}>
        <option value="">Não vincular</option>
        <optgroup label="Produtos">{targets.filter((t) => t.kind === "product").map((t) => <option key={key(t)} value={key(t)}>{t.name}{t.sales_code ? ` (atual ${t.sales_code})` : ""}</option>)}</optgroup>
        <optgroup label="Fichas técnicas">{targets.filter((t) => t.kind === "recipe").map((t) => <option key={key(t)} value={key(t)}>{t.name}{t.sales_code ? ` (atual ${t.sales_code})` : ""}</option>)}</optgroup>
      </select>
    </div>
  );
}

function Uploader({ onFile, busy }) {
  const ref = useRef();
  return (
    <Card className="flex flex-col gap-3" data-testid="codes-upload-card">
      <div className="h3">Importar códigos do caixa</div>
      <p className="caption">Envie a planilha do caixa (.xlsx) com as colunas “Nome Prod” e “Cód. Prod”. O app procura o produto ou a ficha técnica pelo nome parecido · você confere antes de salvar.</p>
      <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" data-testid="codes-file-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} />
      <Button icon={Upload} loading={busy} className="self-start" onClick={() => ref.current.click()} data-testid="codes-upload-button">Escolher planilha</Button>
    </Card>
  );
}

function initial(rows) {
  const choice = {};
  const checked = {};
  const best = {};
  rows.forEach((r) => {
    choice[r.code] = key(r.match);
    const k = key(r.match);
    if (r.match && r.score >= 0.85 && !r.already && !r.code_owner && !r.match.sales_code && (!best[k] || r.score > best[k].score)) best[k] = r;
  });
  Object.values(best).forEach((r) => { checked[r.code] = true; });
  return { choice, checked };
}

function DupWarning() {
  return <div className="caption flex items-center gap-1 mt-1" style={{ color: "var(--danger)" }}><AlertTriangle size={12} /> Outra linha marcada aponta para o mesmo item</div>;
}

export default function PdvCodes() {
  const qc = useQueryClient();
  const [data, setData] = useState(null);
  const [state, setState] = useState({ choice: {}, checked: {} });
  const [filter, setFilter] = useState("all");
  const [skipped, setSkipped] = useState([]);
  const preview = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append("file", file); return api("/sales-codes/preview", { method: "POST", form: fd }); },
    onSuccess: (d) => { setData(d); setState(initial(d.rows)); setSkipped([]); },
    onError: (e) => toast.error(e.message),
  });
  const selected = useMemo(() => (data ? data.rows.filter((r) => state.checked[r.code] && state.choice[r.code]) : []), [data, state]);
  const dups = useMemo(() => {
    const seen = {};
    selected.forEach((r) => { seen[state.choice[r.code]] = (seen[state.choice[r.code]] || 0) + 1; });
    return new Set(selected.filter((r) => seen[state.choice[r.code]] > 1).map((r) => r.code));
  }, [selected, state.choice]);
  const apply = useMutation({
    mutationFn: () => api("/sales-codes/apply", { method: "POST", body: { items: selected.map((r) => { const [kind, id] = state.choice[r.code].split(":"); return { kind, id, code: r.code, name: r.name }; }) } }),
    onSuccess: (d) => {
      ["products", "recipes"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setSkipped(d.skipped);
      const failed = new Set(d.skipped.map((s) => s.code));
      const sent = new Set(selected.map((r) => r.code));
      const rest = data.rows.filter((r) => !sent.has(r.code) || failed.has(r.code));
      const saved = Object.fromEntries(selected.filter((r) => !failed.has(r.code)).map((r) => [state.choice[r.code], r.code]));
      const targets = data.targets.map((t) => (saved[key(t)] ? { ...t, sales_code: saved[key(t)] } : t));
      const rows = rest.map((r) => (r.match && saved[key(r.match)] ? { ...r, match: { ...r.match, sales_code: saved[key(r.match)] } } : r));
      setState((s) => ({ ...s, checked: Object.fromEntries(Object.entries(s.checked).map(([c, v]) => [c, v && !failed.has(c) && !sent.has(c)])) }));
      setData(rows.length ? { rows, targets } : null);
      toast.success(`${d.updated} código(s) salvos${d.skipped.length ? ` · ${d.skipped.length} ignorado(s)` : ""}`);
    },
    onError: (e) => toast.error(e.message),
  });
  const list = useMemo(() => (data?.rows || []).filter((r) => filter === "all" || (filter === "none" ? !r.match : r.match && (r.score < 0.85 || r.code_owner))), [data, filter]);
  const set = (code, patch) => setState((s) => ({ choice: { ...s.choice, ...(patch.choice !== undefined ? { [code]: patch.choice } : {}) }, checked: { ...s.checked, ...(patch.checked !== undefined ? { [code]: patch.checked } : {}) } }));
  return (
    <Page>
      <PageHeader title="Códigos PDV em massa" subtitle="Preencha os códigos do caixa de produtos e fichas de uma vez" back="/products" />
      <div className="flex flex-col gap-5">
        <Uploader onFile={(f) => preview.mutate(f)} busy={preview.isPending} />
        {skipped.length > 0 && (
          <Card data-testid="codes-skipped"><div className="h3 mb-2">Não salvos ({skipped.length})</div>{skipped.map((s) => <div key={s.code + s.name} className="caption py-0.5">{s.name} · {s.code}: {s.reason}</div>)}</Card>
        )}
        {data && (
          <Card className="flex flex-col gap-3" data-testid="codes-preview">
            <div className="flex flex-wrap items-center gap-2">
              <div className="h3 flex-1">{data.rows.length} linha(s) na planilha</div>
              {FILTERS.map((f) => <Chip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} data-testid={`codes-filter-${f.id}`}>{f.label}</Chip>)}
            </div>
            <div className="divide">
              {list.map((r) => <CodeRow key={r.code} row={r} targets={data.targets} choice={state.choice[r.code]} checked={!!state.checked[r.code]} dup={dups.has(r.code)}
                setChoice={(v) => set(r.code, { choice: v, checked: !!v })} toggle={() => set(r.code, { checked: !state.checked[r.code] })} />)}
              {!list.length && <p className="caption py-3">Nada neste filtro.</p>}
            </div>
          </Card>
        )}
      </div>
      {data && (
        <StickyFooter>
          {dups.size > 0 && <span className="caption self-center" style={{ color: "var(--danger)" }} data-testid="codes-dup-warning">Desmarque as linhas repetidas para salvar</span>}
          <Button icon={Check} loading={apply.isPending} disabled={!selected.length || dups.size > 0} onClick={() => apply.mutate()} data-testid="codes-apply-button">Salvar {selected.length} código(s)</Button>
        </StickyFooter>
      )}
    </Page>
  );
}
